import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, OnChanges, signal, SimpleChanges } from '@angular/core';
import { ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';
import { ICellRendererParams } from 'ag-grid-enterprise';
import { AGTableBase } from '../../../shared/components/table/ag-table-base';
import { Destination } from '../../../shared/models/destination.model';
import { SharedLinkTestResultInfo, SharedTestsResultsResponse, SharedTestType } from '../../../shared/models/share-tests.model';
import { DlrRecStatusRenderUtils } from '../../../shared/services/dlr-rec-status-render-utils';
import { TestResultUtils } from '../../../shared/services/test-result-utils';

@Component({
  standalone: false,
  selector: 'app-shared-tests-table',
  templateUrl: './shared-tests-table.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SharedTestsTableComponent extends AGTableBase implements OnChanges {
  readonly datePipe = inject(DatePipe);

  constructor() {
    super();
  }

  readonly sharedTestsResultsInfo = input<SharedTestsResultsResponse>(null);

  readonly columnDefs = signal<ColDef[]>([]);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sharedTestsResultsInfo']) {
      const sharedTestsResultsInfo = this.sharedTestsResultsInfo();

      if (!sharedTestsResultsInfo) {
        this.totalRowsCount = 0;
        return;
      }

      this.totalRowsCount = sharedTestsResultsInfo.testsResults.length;
      this.columnDefs.set(this.buildColumns(sharedTestsResultsInfo.testType));
      this.currentPageNumber = 1;
      this.update();
    }
  }

  update() {
    if (!this.gridApi) {
      return;
    }

    const gridApi = this.gridApi as GridApi & {
      paginationSetPageSize?: (size: number) => void;
    };

    gridApi.paginationSetPageSize?.(this.paginationPageSize);
    this.gridApi.paginationGoToPage(this.currentPageNumber - 1);
  }

  override changeSize($event, size) {
    this.paginationPageSize = size;
    this.currentPageNumber = 1;
    this.update();
  }

  override onPageChange(event) {
    if (event === this.currentPageNumber) {
      return;
    }

    this.currentPageNumber = event;
    this.update();
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.update();
  }

  private buildColumns(testType: SharedTestType) {
    const isMntTest = testType === SharedTestType.MNT;

    const coldDef: ColDef<SharedLinkTestResultInfo>[] = [
      {
        colId: 'date',
        headerTooltip: 'Date when the test was triggered',
        field: 'createdAt',
        headerName: 'Date',
        resizable: false,
        maxWidth: 120,
        suppressMovable: true,
        valueFormatter: params => this.datePipe.transform(params.value, 'dd/MM/yyyy'),
      },
      {
        headerName: 'Country(MCC)/Network(MNC)',
        headerTooltip: 'The destination to which the test was sent',
        resizable: false,
        suppressMovable: true,
        cellRenderer: (params: ICellRendererParams<SharedLinkTestResultInfo>) => {
          return `<div class="two-lines">${TestResultUtils.renderDestination(this.buildDestinationInfo(params.data), true)}</div>`;
        },
      },
      {
        headerName: isMntTest ? 'Receipt status' : 'DLR/Rec. Status',
        maxWidth: 90,
        resizable: false,
        suppressMovable: true,
        headerTooltip: isMntTest
          ? 'Receipt status of the test, indicating whether the test was delivered to our test number'
          : 'DLR status - Delivery Report received from the supplier via SMPP, Rec.status - Receipt Status received from our test number.',
        cellClass: 'd-flex align-items-center justify-content-center',
        cellRenderer: ({ data }: ICellRendererParams<SharedLinkTestResultInfo>) => {
          return isMntTest
            ? DlrRecStatusRenderUtils.formatRecStatus(data.testStatus, data.channel)
            : DlrRecStatusRenderUtils.formatDlrRecStatus(data.testStatus, data.messageState, data.channel);
        },
      },
      {
        headerName: 'Sender ID',
        resizable: false,
        suppressMovable: true,
        headerTooltip: 'Sender ID of the delivered SMS',
        cellRenderer: (params: ICellRendererParams<SharedLinkTestResultInfo>) => {
          const row = params.data;
          return isMntTest
            ? `<div class="one-line">${row.senderDelivered || ''}</div>`
            : TestResultUtils.renderSenderDelivered(row.senderDelivered, row.senderSent);
        },
      },
      {
        headerName: 'Text',
        resizable: false,
        suppressMovable: true,
        headerTooltip: 'Text of the delivered SMS',
        cellRenderer: (params: ICellRendererParams<SharedLinkTestResultInfo>) => {
          const row = params.data;

          return isMntTest
            ? TestResultUtils.renderMntTextDelivered(row.textDelivered)
            : TestResultUtils.renderLntText(row.textDeliveredFull, row.textSentBackend);
        },
      },
      {
        headerName: 'Phone/SMSC',
        resizable: false,
        suppressMovable: true,
        headerTooltip: 'Test number, to which the test SMS was sent/country ISO of delivering SMSC',
        cellRenderer: (params: ICellRendererParams<SharedLinkTestResultInfo>) => {
          const row = params.data;

          const destination = this.buildDestinationInfo(row);
          const smscInfo = this.buildSmscInfo(row);

          return isMntTest
            ? TestResultUtils.renderMntPhoneSmscInfo(destination, smscInfo)
            : TestResultUtils.renderLntPhoneSmscInfo(destination, smscInfo);
        },
      },
      {
        resizable: false,
        suppressMovable: true,
        headerName: 'Company',
        headerTooltip: 'The company that requested this test',
        cellRenderer: (params: ICellRendererParams<SharedLinkTestResultInfo>) => {
          const row = params.data;
          return `<div class="one-line">${row.companyName || '-'}</div>`;
        },
      },
      {
        resizable: false,
        suppressMovable: true,
        maxWidth: 140,
        headerName: isMntTest ? 'Receipt delay' : 'DLR/Rec. delay',
        headerTooltip: isMntTest
          ? 'The time interval between the creation of a test and the delivery of a test to our server'
          : 'Delays in getting the SMPP DLR status and Receipt status',
        cellRenderer: (params: ICellRendererParams<SharedLinkTestResultInfo>) => {
          const row = params.data;
          return `<div class="two-lines ">${TestResultUtils.renderDelay(row.receiptDelay, row.smppDelay)}</div>`;
        },
      },
    ];

    return coldDef;
  }

  private buildDestinationInfo(testResult: SharedLinkTestResultInfo): Destination {
    return {
      countryName: testResult.destinationCountryName,
      mcc: testResult.destinationMcc,
      mnc: testResult.destinationMnc,
      originalMnc: testResult.destinationOriginalMnc,
      originalProviderName: testResult.destinationOriginalProviderName,
      phone: testResult.destinationPhoneMasked,
      providerName: testResult.destinationProviderName,
    };
  }

  private buildSmscInfo(testResult: SharedLinkTestResultInfo) {
    return {
      countryCode: testResult.smscCountryCode,
      countryName: testResult.smscCountryName,
      phone: testResult.smscPhoneMasked,
    };
  }
}
